import type { CommentBody, ThreadData } from '@liveblocks/client'
import type { MutableRefObject } from 'react'
import { SortBy } from './comments-select'

export const ListType = {
  DropDown: 'drop-down',
  DrillDown: 'drill-down',
  Dependent: 'dependent',
} as const

export type ListType = (typeof ListType)[keyof typeof ListType]

interface ListValue {
  id: string
  row_number: number
  value: string | null
}

interface ListColumn {
  id: string
  name: string
  precedence: number
  list_values: ListValue[]
}

export interface List {
  id?: string | null
  name?: string
  powered_template_id?: string | null
  list_columns: ListColumn[]
  type?: ListType
  transformedColumns?: unknown
  notes?: string
  updated_at?: string
}

export interface ListApiResponse {
  id: string
  name: string
  notes: string
  type: ListType
  powered_template_id: string | null
  list_columns: ListColumn[]
  updated_at: string
}

export interface ListResponse {
  id: string
  name: string
  type: ListType
  notes: string
  updated_at: string
  powered_template_id: string
  list_columns: {
    id: string
    list_id: string
    name: string
    precedence: number
    list_values: {
      id: string
      list_id: string
      row_number: number
      value: string
    }[]
  }[]
}

export interface CommentMetadata {
  status: 'pending' | 'approved' | 'rejected'
  userId: string
  userName: string
  listValueId: string
  resolved?: boolean
  moderatedAt?: string
  moderatedById?: string
  moderatedByName?: string
  type?: 'cell' | 'general'
  updatedAt: string
  prevValue: string
  currentValue: string
}

export type QueuedResolutionStatus = 'approved' | 'rejected'

export interface QueuedResolution {
  thread: ThreadData
  status: QueuedResolutionStatus
}

interface EditListBaseProps {
  shouldPersist?: () => Promise<boolean>
  afterSave?: (isPoweredTemplate: boolean) => Promise<void>
}

export interface LiveblocksEditListProps extends EditListBaseProps {
  threads: ThreadData[]
  activeCommentTarget: {
    id: string
    position: { top: number; left: number } | null
  } | null
  setActiveCommentTarget: (
    target: { id: string; position: { top: number; left: number } | null } | null,
  ) => void
  hasPendingComments: boolean
  checkPendingStatus: (threads: ThreadData[]) => boolean
  updateLocalThreadStatus: (threadId: string, status: QueuedResolutionStatus) => void
  queueThreadResolution: (thread: ThreadData, status: QueuedResolutionStatus) => void
  handlePendingThreadResolutions: () => Promise<void>
  queuedResolutionsRef: MutableRefObject<Map<string, QueuedResolution>>
  createThread: ({
    body,
    attachments,
    metadata,
  }: {
    body: CommentBody
    attachments?: unknown[]
    metadata: CommentMetadata
  }) => void
  editThreadMetadata: (options: { threadId: string; metadata: CommentMetadata }) => void
  selectedCommentType: SortBy
  setSelectedCommentType: (value: SortBy) => void
  threadsToDisplayInSidePanel: ThreadData[] | []
  hasUnresolvedGeneralComments: boolean
  handleRefetch?: (onRefetch: () => void) => void
  reviewModeHandlers?: {
    isOwner?: boolean
    setLock?: () => void
    releaseLock?: () => void
    isLocked?: boolean
  }
}

export type EditListProps = EditListBaseProps | LiveblocksEditListProps

export interface TemplateField {
  id: string
  field?: {
    id: string
    name: string
    governance_type: string
  }
}

export interface TemplateResponse {
  template_fields?: TemplateField[]
}

export interface DatasetQueryRequest {
  limit: number
  commitRef: string
  distinct: boolean
  select: string[]
}

export interface DatasetQueryResponse {
  data?: {
    rows?: {
      data: Record<string, { value: string }>
    }[]
  }
}

export interface ListTypeOption {
  label: string
  value: ListType
}

export interface FeedbackSubmissionRequest {
  rating: number
  comment?: string
  acceptContact: boolean
}
